import apiService from './apiService';
import streamingService from './streamingService';

/** Server-side cap on `size` for GET /diy-agents — larger values 422. */
export const AGENTS_MAX_PAGE_SIZE = 100;

class AgentService {
  // Agent Management
  async createAgent(agentData) {
    try {
      const result = await apiService.createAgent(agentData);
      return result;
    } catch (error) {
      console.error('Error creating agent:', error);
      throw error;
    }
  }

  /**
   * @param {number} size page size. The API caps this at AGENTS_MAX_PAGE_SIZE
   *   and answers anything larger with a 422, so it is clamped here rather than
   *   left for each caller to remember. To fetch everything, read `total_pages`
   *   off the first response and request the rest.
   */
  async getAgents(page = 1, size = 10, name = null) {
    try {
      const result = await apiService.getAgents(page, Math.min(size, AGENTS_MAX_PAGE_SIZE), name);
      return result;
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  }

  async getAgent(agentId) {
    try {
      const result = await apiService.getAgent(agentId);
      return result;
    } catch (error) {
      console.error('Error fetching agent:', error);
      throw error;
    }
  }

  async updateAgent(agentId, agentData) {
    try {
      const result = await apiService.updateAgent(agentId, agentData);
      return result;
    } catch (error) {
      console.error('Error updating agent:', error);
      throw error;
    }
  }

  async deleteAgent(agentId) {
    try {
      const result = await apiService.deleteAgent(agentId);
      return result;
    } catch (error) {
      console.error('Error deleting agent:', error);
      throw error;
    }
  }

  async shareAgent(agentId, email) {
    try {
      const result = await apiService.shareAgent(agentId, email);
      return result;
    } catch (error) {
      console.error('Error sharing agent:', error);
      throw error;
    }
  }

  async getAgentHistory(agentId) {
    try {
      const result = await apiService.getAgentHistory(agentId);
      return result;
    } catch (error) {
      console.error('Error fetching agent history:', error);
      throw error;
    }
  }

  async getAgentLearningConfig(agentId) {
    try {
      const result = await apiService.getAgentLearningConfig(agentId);
      return result;
    } catch (error) {
      console.error('Error fetching learning config:', error);
      throw error;
    }
  }

  async updateAgentLearningConfig(agentId, learningConfig) {
    try {
      const result = await apiService.updateAgentLearningConfig(agentId, learningConfig);
      return result;
    } catch (error) {
      console.error('Error updating learning config:', error);
      throw error;
    }
  }

  async getAgentLearningRecords(agentId, params = {}) {
    try {
      const result = await apiService.getAgentLearningRecords(agentId, params);
      return result;
    } catch (error) {
      console.error('Error fetching learning records:', error);
      throw error;
    }
  }

  async deleteAgentLearningRecord(agentId, learningId) {
    try {
      const result = await apiService.deleteAgentLearningRecord(agentId, learningId);
      return result;
    } catch (error) {
      console.error('Error deleting learning record:', error);
      throw error;
    }
  }

  async restoreAgentVersion(agentId, historyId) {
    try {
      const result = await apiService.restoreAgentVersion(agentId, historyId);
      return result;
    } catch (error) {
      console.error('Error restoring agent version:', error);
      throw error;
    }
  }

  async getChainedVariables(agentIds) {
    try {
      const result = await apiService.getChainedVariables(agentIds);
      return result;
    } catch (error) {
      console.error('Error fetching chained variables:', error);
      throw error;
    }
  }

  async createAgentTeam(teamData) {
    try {
      return await apiService.createAgentTeam(teamData);
    } catch (error) {
      console.error('Error creating agent team:', error);
      throw error;
    }
  }

  async getAgentTeam(teamId) {
    try {
      return await apiService.getAgentTeam(teamId);
    } catch (error) {
      console.error('Error fetching agent team:', error);
      throw error;
    }
  }

  async getAgentTeamByAgent(agentId) {
    try {
      return await apiService.getAgentTeamByAgent(agentId);
    } catch (error) {
      console.error('Error fetching agent team by agent:', error);
      throw error;
    }
  }

  async updateAgentTeam(teamId, teamData) {
    try {
      return await apiService.updateAgentTeam(teamId, teamData);
    } catch (error) {
      console.error('Error updating agent team:', error);
      throw error;
    }
  }

  async deleteAgentTeam(teamId) {
    try {
      return await apiService.deleteAgentTeam(teamId);
    } catch (error) {
      console.error('Error deleting agent team:', error);
      throw error;
    }
  }

  async publishAgent(agentId) {
    try {
      const result = await apiService.publishAgent(agentId);
      return result;
    } catch (error) {
      console.error('Error publishing agent:', error);
      throw error;
    }
  }

  async unpublishAgent(agentId) {
    try {
      const result = await apiService.unpublishAgent(agentId);
      return result;
    } catch (error) {
      console.error('Error unpublishing agent:', error);
      throw error;
    }
  }

  async publishAsTemplate(agentId, templateDescription = null) {
    try {
      const result = await apiService.publishAsTemplate(agentId, templateDescription);
      return result;
    } catch (error) {
      console.error('Error publishing agent as template:', error);
      throw error;
    }
  }

  async deleteTemplate(agentId) {
    try {
      const result = await apiService.deleteTemplate(agentId);
      return result;
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  async getTemplates(page = 1, size = 10) {
    try {
      const result = await apiService.getTemplates(page, size);
      return result;
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  }

  async getTemplateByAgentId(agentId) {
    try {
      const result = await apiService.getTemplateByAgentId(agentId);
      return result;
    } catch (error) {
      console.error('Error fetching template by agent ID:', error);
      throw error;
    }
  }

  async executeAgent(agentId, query, variables = {}, sessionId = null, streaming = true) {
    try {
      const payload = {
        agent_id: agentId,
        query: query,
        variables: variables,
        session_id: sessionId || this.generateSessionId(),
        streaming: streaming
      };

      const result = await apiService.executeAgent(payload);
      return result;
    } catch (error) {
      console.error('Error executing agent:', error);
      throw error;
    }
  }

  /**
   * Execute agent with streaming response
   * @param {string} agentId - The agent ID
   * @param {string} query - The user query
   * @param {Object} variables - Variables to pass to the agent
   * @param {string} sessionId - Session ID for conversation continuity
   * @param {string} userLocation - User's location
   * @param {string} userTimezone - User's timezone
   * @param {Array<File>} files - Files to upload and process
   * @param {Function} onMessage - Callback for each message chunk
   * @param {Function} onError - Callback for errors
   * @param {Function} onComplete - Callback when stream completes
   * @param {Function} onRunMeta - Optional callback when run_id is known
   * @returns {Promise} - Promise that resolves when stream starts
   */
  async executeAgentStream(agentId, query, variables = {}, sessionId = null, userLocation = null, userTimezone = null, files = [], onMessage, onError, onComplete, onRunMeta, filtersOrOptions = null, streamingArg = true, maybeFilters = null, maybeStreaming = true) {
    try {
      let filters = filtersOrOptions;
      let streaming = streamingArg;
      let hilCallbacks = null;
      let onBackgroundStarted = null;
      let teamCallbacks = null;
      let loopCallbacks = null;
      let onToolWarning = null;
      let frameCallbacks = null;
      let qualification = null;
      let qualificationCallbacks = null;

      if (
        filtersOrOptions &&
        typeof filtersOrOptions === 'object' &&
        (
          Object.prototype.hasOwnProperty.call(filtersOrOptions, 'filters') ||
          Object.prototype.hasOwnProperty.call(filtersOrOptions, 'streaming') ||
          Object.prototype.hasOwnProperty.call(filtersOrOptions, 'hilCallbacks') ||
          Object.prototype.hasOwnProperty.call(filtersOrOptions, 'onBackgroundStarted') ||
          Object.prototype.hasOwnProperty.call(filtersOrOptions, 'teamCallbacks') ||
          Object.prototype.hasOwnProperty.call(filtersOrOptions, 'loopCallbacks')
          || Object.prototype.hasOwnProperty.call(filtersOrOptions, 'onToolWarning')
          || Object.prototype.hasOwnProperty.call(filtersOrOptions, 'onSources')
          || Object.prototype.hasOwnProperty.call(filtersOrOptions, 'onFollowups')
          || Object.prototype.hasOwnProperty.call(filtersOrOptions, 'onRunError')
          || Object.prototype.hasOwnProperty.call(filtersOrOptions, 'qualification')
          || Object.prototype.hasOwnProperty.call(filtersOrOptions, 'qualificationCallbacks')
        )
      ) {
        filters = filtersOrOptions.filters || null;
        streaming = filtersOrOptions.streaming !== undefined ? filtersOrOptions.streaming : true;
        hilCallbacks = filtersOrOptions.hilCallbacks || null;
        onBackgroundStarted = filtersOrOptions.onBackgroundStarted || null;
        teamCallbacks = filtersOrOptions.teamCallbacks || null;
        loopCallbacks = filtersOrOptions.loopCallbacks || null;
        onToolWarning = filtersOrOptions.onToolWarning || null;
        frameCallbacks = {
          onSources: filtersOrOptions.onSources || null,
          onFollowups: filtersOrOptions.onFollowups || null,
          onRunError: filtersOrOptions.onRunError || null,
        };
        qualification = filtersOrOptions.qualification || null;
        qualificationCallbacks = filtersOrOptions.qualificationCallbacks || null;
      } else if (typeof streamingArg === 'function') {
        onBackgroundStarted = streamingArg;
        filters = maybeFilters;
        streaming = maybeStreaming;
      }

      return await streamingService.executeAgentStream(
        agentId,
        query,
        variables,
        sessionId,
        userLocation,
        userTimezone,
        files,
        onMessage,
        onError,
        onComplete,
        onRunMeta,
        hilCallbacks,
        onBackgroundStarted,
        filters,
        streaming,
        teamCallbacks,
        loopCallbacks,
        onToolWarning,
        frameCallbacks,
        qualification,
        qualificationCallbacks
      );
    } catch (error) {
      console.error('Error executing agent stream:', error);
      throw error;
    }
  }

  /**
   * Re-answer the same question, replacing the original in history.
   * Needs memory + checkpointing on the agent; runs stay continuable for 7 days.
   * @param {string} runId - run_id from the run_meta frame of the run to redo
   * @param {Object} callbacks - same shape as executeAgentStream's options
   */
  async regenerateRun(runId, callbacks = {}) {
    return streamingService.continueRunStream(
      `/dynamic-agent/runs/${runId}/regenerate`,
      null,
      callbacks
    );
  }

  /**
   * Branch from an existing run ("edit & re-run from here"). The original run
   * stays intact.
   * @param {string} runId - run_id of the run to branch from
   * @param {Object} params - { input, continueFrom }
   * @param {string} [params.input] - replacement message; omit to replay the original
   * @param {string|number} [params.continueFrom] - "last_user" (default), "end", or a message index
   * @param {Object} callbacks - same shape as executeAgentStream's options
   */
  async forkRun(runId, { input, continueFrom } = {}, callbacks = {}) {
    const body = { continue_from: continueFrom ?? 'last_user' };
    if (typeof input === 'string' && input.trim() !== '') {
      body.input = input;
    }

    return streamingService.continueRunStream(
      `/dynamic-agent/runs/${runId}/fork`,
      body,
      callbacks
    );
  }

  /**
   * Start a background (non-streaming) run. Resolves as soon as the run is
   * queued; collect the answer with pollRunStatus.
   */
  async executeAgentBackground(agentId, query, variables = {}, sessionId = null, userLocation = null, userTimezone = null, filters = null, qualification = null) {
    try {
      return await apiService.executeAgentBackground(
        agentId,
        query,
        variables,
        sessionId,
        userLocation,
        userTimezone,
        filters,
        qualification
      );
    } catch (error) {
      console.error('Error starting background agent run:', error);
      throw error;
    }
  }

  async getRunStatus(runId) {
    try {
      return await apiService.getAgentRunStatus(runId);
    } catch (error) {
      console.error('Error fetching run status:', error);
      throw error;
    }
  }

  /**
   * Poll a background run until it finishes. There is no partial text — the
   * whole answer arrives at once — so this backs off from 2s up to 10s.
   * @returns {Function} - call to stop polling
   */
  pollRunStatus(runId, { onStatusUpdate, onComplete, onError, maxPolls = 300 } = {}) {
    let isPolling = true;
    let pollCount = 0;
    let delay = 2000;

    const poll = async () => {
      if (!isPolling) return;

      if (pollCount >= maxPolls) {
        isPolling = false;
        onError?.(new Error('The background run is taking longer than expected. Check back later.'));
        return;
      }

      try {
        const status = await apiService.getAgentRunStatus(runId);
        if (!isPolling) return;

        onStatusUpdate?.(status);

        if (status?.status === 'completed') {
          isPolling = false;
          onComplete?.(status);
          return;
        }
        if (status?.status === 'failed' || status?.status === 'error') {
          isPolling = false;
          onError?.(new Error(status?.message || status?.error || 'The background run failed.'));
          return;
        }
      } catch (error) {
        if (!isPolling) return;
        console.error('Background run polling error:', error);
        // Keep polling through transient failures; maxPolls bounds the wait.
      }

      pollCount += 1;
      delay = Math.min(10000, Math.round(delay * 1.5));
      setTimeout(poll, delay);
    };

    poll();

    return () => {
      isPolling = false;
    };
  }

  // Agent metrics
  async getRunMetrics(runId) {
    try {
      return await apiService.getAgentMetricsRun(runId);
    } catch (error) {
      console.error('Error fetching run metrics:', error);
      throw error;
    }
  }

  async getAgentUsage(window = '24h', agentId = null) {
    try {
      return await apiService.getAgentMetricsAgents(window, agentId);
    } catch (error) {
      console.error('Error fetching agent usage:', error);
      throw error;
    }
  }

  async getModelUsage(window = '24h', agentId = null) {
    try {
      return await apiService.getAgentMetricsModels(window, agentId);
    } catch (error) {
      console.error('Error fetching model usage:', error);
      throw error;
    }
  }

  /**
   * Close an active stream
   * @param {string} sessionId - Session ID of the stream to close
   */
  closeStream(sessionId) {
    streamingService.closeStream(sessionId);
  }

  /**
   * Close all active streams
   */
  closeAllStreams() {
    streamingService.closeAllStreams();
  }

  generateSessionId() {
    return 'session_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }

  /**
   * Start Human-in-Loop workflow
   * @param {number} agentId - The agent ID
   * @param {string} query - The user query
   * @param {Object} variables - Variables to pass to the agent
   * @param {string} sessionId - Session ID for conversation continuity
   * @param {string} userLocation - User's location
   * @param {string} userTimezone - User's timezone
   * @param {Array<File>} files - Files to upload
   * @param {Object} callbacks - Callbacks for HIL events
   * @returns {Promise} - Promise that resolves when workflow starts
   */
  async startHILWorkflow(agentId, query, variables = {}, sessionId = null, userLocation = 'India', userTimezone = 'Asia/Kolkata', files = [], callbacks = {}, filters = null, streaming = true, qualification = null) {
    try {
      const response = await apiService.startHILWorkflowWithUpload(
        agentId,
        query,
        files,
        variables,
        sessionId,
        userLocation,
        userTimezone,
        filters,
        streaming,
        qualification
      );

      await streamingService.processHILWorkflowStream(response, callbacks);
    } catch (error) {
      console.error('Error starting HIL workflow:', error);
      if (callbacks.onError) {
        callbacks.onError({ message: error.message || 'Failed to start HIL workflow' });
      }
      throw error;
    }
  }

  /**
   * Continue Human-in-Loop workflow after approval
   * @param {string} runId - The workflow run ID
   * @param {number} groupIndex - The step index being approved
   * @param {Object} approvedOutput - The approved/modified output
   * @param {Object} callbacks - Callbacks for HIL events
   * @returns {Promise} - Promise that resolves when workflow continues
   */
  async continueHILWorkflow(runId, groupIndex, approvedOutput, callbacks = {}) {
    try {
      const response = await apiService.continueHILWorkflow(runId, groupIndex, approvedOutput);
      await streamingService.processHILWorkflowStream(response, callbacks);
    } catch (error) {
      console.error('Error continuing HIL workflow:', error);
      if (callbacks.onError) {
        callbacks.onError({ message: error.message || 'Failed to continue HIL workflow' });
      }
      throw error;
    }
  }

  async continueTeamHIL(runId, approvedOutput, callbacks = {}) {
    try {
      const response = await apiService.continueTeamHIL(runId, approvedOutput);
      await streamingService.processHILWorkflowStream(response, callbacks);
    } catch (error) {
      console.error('Error continuing team HIL:', error);
      if (callbacks.onError) {
        callbacks.onError({ message: error.message || 'Failed to continue team run' });
      }
      throw error;
    }
  }

  /**
   * Get HIL workflow status
   * @param {string} runId - The workflow run ID
   * @returns {Promise} - Promise that resolves with status
   */
  async getHILWorkflowStatus(runId) {
    try {
      return await apiService.getHILWorkflowStatus(runId);
    } catch (error) {
      console.error('Error fetching HIL workflow status:', error);
      throw error;
    }
  }

  async getTeamHILStatus(runId) {
    try {
      return await apiService.getTeamHILStatus(runId);
    } catch (error) {
      console.error('Error fetching team HIL status:', error);
      throw error;
    }
  }

  /**
   * Regenerate Human-in-Loop workflow step
   * @param {string} runId - The workflow run ID
   * @param {number} waitingGroupIndex - The step index to regenerate
   * @param {string} regenerateInput - The new input for regeneration
   * @param {Object} callbacks - Callbacks for HIL events
   * @returns {Promise} - Promise that resolves when regeneration completes
   */
  async regenerateHILWorkflow(runId, waitingGroupIndex, regenerateInput, callbacks = {}) {
    try {
      const response = await apiService.regenerateHILWorkflow(runId, waitingGroupIndex, regenerateInput);
      await streamingService.processHILWorkflowStream(response, callbacks);
    } catch (error) {
      console.error('Error regenerating HIL workflow:', error);
      if (callbacks.onError) {
        callbacks.onError({ message: error.message || 'Failed to regenerate HIL workflow step' });
      }
      throw error;
    }
  }

  async regenerateTeamHIL(runId, callbacks = {}) {
    try {
      const response = await apiService.regenerateTeamHIL(runId);
      await streamingService.processHILWorkflowStream(response, callbacks);
    } catch (error) {
      console.error('Error regenerating team HIL member:', error);
      if (callbacks.onError) {
        callbacks.onError({ message: error.message || 'Failed to regenerate team member' });
      }
      throw error;
    }
  }

  /**
   * Poll background workflow status
   * @param {string} workflowRunId - The background workflow run ID
   * @param {Function} onStatusUpdate - Callback for status updates
   * @param {Function} onComplete - Callback when completed
   * @param {Function} onError - Callback for errors
   * @param {number} pollInterval - Polling interval in ms (default 2000)
   * @param {number} maxPolls - Maximum number of polls (default 300)
   * @returns {Function} - Function to stop polling
   */
  async pollBackgroundStatus(workflowRunId, onStatusUpdate, onComplete, onError, pollInterval = 2000, maxPolls = 300) {
    let pollCount = 0;
    let isPolling = true;

    const poll = async () => {
      if (!isPolling || pollCount >= maxPolls) {
        if (pollCount >= maxPolls && onError) {
          onError(new Error('Workflow timeout'));
        }
        return;
      }

      try {
        const status = await apiService.getBackgroundWorkflowStatus(workflowRunId);

        if (onStatusUpdate) {
          onStatusUpdate(status);
        }

        if (status.status === 'completed') {
          if (onComplete) {
            onComplete(status);
          }
          isPolling = false;
          return;
        } else if (status.status === 'failed') {
          if (onError) {
            onError(new Error('Workflow failed'));
          }
          isPolling = false;
          return;
        } else {
          // Still running, poll again
          pollCount++;
          setTimeout(poll, pollInterval);
        }
      } catch (error) {
        console.error('Polling error:', error);
        pollCount++;
        if (pollCount >= maxPolls) {
          if (onError) {
            onError(error);
          }
          isPolling = false;
        } else {
          setTimeout(poll, pollInterval);
        }
      }
    };

    poll();

    // Return stop function
    return () => {
      isPolling = false;
    };
  }

  async pollTeamBackgroundStatus(runId, onStatusUpdate, onComplete, onError, pollInterval = 4000, maxPolls = 300) {
    let pollCount = 0;
    let isPolling = true;
    let nextDelay = pollInterval;

    const poll = async () => {
      if (!isPolling || pollCount >= maxPolls) {
        if (pollCount >= maxPolls && onError) {
          onError(new Error('Team run timeout'));
        }
        return;
      }

      try {
        const status = await apiService.getTeamBackgroundStatus(runId);
        onStatusUpdate?.(status);

        if (status.status === 'completed') {
          onComplete?.(status);
          isPolling = false;
          return;
        }
        if (status.status === 'error' || status.status === 'failed') {
          onError?.(new Error(status.message || 'Team run failed'));
          isPolling = false;
          return;
        }

        pollCount += 1;
        nextDelay = Math.min(30000, Math.round(nextDelay * 1.25));
        setTimeout(poll, nextDelay);
      } catch (error) {
        console.error('Team background polling error:', error);
        pollCount += 1;
        if (pollCount >= maxPolls) {
          onError?.(error);
          isPolling = false;
        } else {
          setTimeout(poll, nextDelay);
        }
      }
    };

    poll();

    return () => {
      isPolling = false;
    };
  }

  // Helper method to format agent data for API
  formatAgentData(formData, features, examples, structuredOutput, knowledgeBaseData, memoryData, agentChainingData, extractedVariables, temperature, topP, providers, models, knowledgeBaseD3Data, tools, responsibleAiConfig, agentTeamData, contextProviders, knowledgeGraphData) {
    const variablesString = extractedVariables.join(", ");

    // Knowledge Graph datasets ride on the D3 knowledge-base channel: a dataset
    // owns exactly one D3 folder, and attaching that folder is what gives the
    // agent its KG tools. There is no separate attachment endpoint, so the
    // dataset's folder_id is merged into `knowledge_base_dim3.knowledge_bases`
    // alongside any ordinary D3 folders. `__kg_dataset_id` is a client-side
    // marker only — reading the config back cross-references folder ids against
    // the dataset list rather than trusting it.
    const knowledgeGraphEntries =
      Boolean(features.knowledgeGraph) && Array.isArray(knowledgeGraphData?.datasets)
        ? knowledgeGraphData.datasets
          .filter((entry) => entry && entry.folder_id !== undefined && entry.folder_id !== null)
          .map((entry) => ({
            id: `kg-${entry.dataset_id}`,
            knowledgeBase: [entry.folder_id],
            numberOfChunks: 5,
            retrievalType: "Basic",
            scoreThreshold: 0.0,
            __kg_dataset_id: entry.dataset_id,
          }))
        : [];

    const dim3Entries = [
      ...(Boolean(features.knowledgeBaseD3) && knowledgeBaseD3Data
        ? knowledgeBaseD3Data.knowledgeBases || []
        : []),
      ...knowledgeGraphEntries,
    ];

    // Find provider and model details
    const selectedProvider = providers.find(p => p.id === formData.llmProvider);
    const selectedModel = models.find(m => m.id === formData.llmModel);

    // Responsible AI:
    // - Always send a top-level `responsible_ai_config` object (backend expects it)
    // - When feature is OFF, send ONLY `{ enabled: false }` to avoid ambiguity
    // - When feature is ON, preserve the config object but force `enabled: true`
    const responsibleAiEnabled = Boolean(features.responsibleAi);
    let normalizedResponsibleAiConfig = { enabled: responsibleAiEnabled };
    if (responsibleAiEnabled) {
      normalizedResponsibleAiConfig =
        responsibleAiConfig && typeof responsibleAiConfig === "object" && !Array.isArray(responsibleAiConfig)
          ? { ...responsibleAiConfig, enabled: true }
          : { enabled: true };
    }

    const payload = {
      // Basic agent information
      name: formData.name,
      description: formData.description,
      agent_role: formData.agentRole,
      agent_goal: formData.agentGoal,
      agent_instructions: formData.agentInstructions,
      variables: variablesString,

      // Note: chained_variables are now fetched dynamically via API, not stored

      // LLM configuration with full details
      llm_provider: selectedProvider ? {
        id: selectedProvider.id,
        name: selectedProvider.display_name || selectedProvider.name,
        type: selectedProvider.type || 'unknown'
      } : null,
      llm_model: selectedModel ? {
        id: selectedModel.id,
        name: selectedModel.name || selectedModel.display_name || selectedModel.model_name,
        provider_id: selectedModel.provider_id || formData.llmProvider,
        cost: selectedModel.cost,
        speed: selectedModel.speed,
        context: selectedModel.context,
        special: selectedModel.special
      } : null,

      // Model settings
      temperature: temperature,
      top_p: topP,

      // Features configuration
      features: {
        knowledge_base: Boolean(features.knowledgeBase),
        knowledge_base_d3: Boolean(features.knowledgeBaseD3),
        memory: Boolean(features.memory),
        context: Boolean(features.context),
        agent_chaining: Boolean(features.agentChaining),
        agent_team: Boolean(agentTeamData),
        human_in_loop: Boolean(features.humanInLoop),
        background_run: Boolean(features.backgroundRun),
        tools: Boolean(features.tools),
        responsible_ai: Boolean(features.responsibleAi),
        // Faceted KG datasets. Defaults off; existing agents are unaffected.
        knowledge_graph: Boolean(features.knowledgeGraph),
        // Agno 2.7.4 run-behaviour toggles. An absent key means off on the
        // backend, except show_sources which defaults on — so existing agents
        // keep citations without a backfill.
        show_sources: Boolean(features.showSources),
        followups: Boolean(features.followups),
        prompt_caching: Boolean(features.promptCaching),
        compress_tool_results: Boolean(features.compressToolResults),
        model_fallback: Boolean(features.modelFallback),
        checkpointing: Boolean(features.checkpointing),
        live_sources: Boolean(features.liveSources),
        // DIM1 query enhancements. Independent of one another and absent-means-
        // off on the backend, so existing agents are unaffected. Extraction of
        // the guidance itself runs automatically when any of them is on.
        query_expansion: Boolean(features.queryExpansion),
        contextual_relevance: Boolean(features.contextualRelevance),
        qualifying_questions: Boolean(features.qualifyingQuestions),
      },

      // Live sources — apps the agent looks things up in at run time.
      // Distinct from `tools`, which let the agent act in an app.
      context_providers:
        Boolean(features.liveSources) && Array.isArray(contextProviders)
          ? contextProviders.map((provider) => {
            const entry = {
              toolkit_slug: provider.toolkitSlug || provider.toolkit_slug,
              app_name: provider.appName || provider.app_name || '',
              name: provider.name || provider.appName || provider.app_name || '',
              mode: provider.mode || 'agent',
            };
            const appId = provider.appId ?? provider.app_id;
            if (appId !== undefined && appId !== null && appId !== '') {
              entry.app_id = String(appId);
            }
            const instructions = provider.instructions;
            if (typeof instructions === 'string' && instructions.trim() !== '') {
              entry.instructions = instructions.trim();
            }
            return entry;
          })
          : [],

      // Responsible AI configuration
      // Backend expects this at the top-level payload
      responsible_ai_config: normalizedResponsibleAiConfig,

      // Knowledge base configuration
      knowledge_base: Boolean(features.knowledgeBase) && knowledgeBaseData ? {
        rag_type: knowledgeBaseData.ragType,
        knowledge_bases: knowledgeBaseData.knowledgeBases || []
      } : null,

      // Knowledge base (D3) configuration — carries both ordinary D3 folders and
      // any attached Knowledge Graph datasets' folders (see above). Emitted
      // whenever either produced an entry, so a KG-only agent still gets one.
      knowledge_base_dim3: dim3Entries.length > 0 ? {
        rag_type: knowledgeBaseD3Data?.ragType || "RAG",
        knowledge_bases: dim3Entries
      } : null,

      // Memory configuration - fix the data structure
      memory: Boolean(features.memory) && memoryData ? {
        max_messages: memoryData.maxMessages || 10,
        memory_type: 'short_term', // Default memory type
        enabled: features.memory
      } : null,

      // Agent chaining configuration
      agent_chaining: Boolean(features.agentChaining) && agentChainingData ? {
        selected_agents: agentChainingData.selectedAgents || [],
        chaining_strategy: agentChainingData.chainingStrategy || 'sequential'
      } : null,

      // Tools configuration
      // Composio rows bind to a toolkit (the agent picks the right action per request).
      // Custom and MCP rows still pin a specific action.
      tools: tools ? tools.map(tool => {
        const backendToolType = tool.toolType || 'composio';

        const toolData = {
          app_id: tool.appId,
          app_name: tool.appName,
          tool_type: backendToolType
        };

        if (backendToolType === 'composio') {
          const slug =
            tool.toolkitSlug ||
            tool.toolkit_slug ||
            (tool.appName || tool.app_name || '').toString().toLowerCase();
          if (slug) toolData.toolkit_slug = slug;
        } else {
          toolData.action_name = tool.actionName;
        }

        // For system configured tools, include the configuration
        if (tool.toolType === 'custom' && tool.customToolConfig) {
          toolData.config = tool.customToolConfig;
        }

        return toolData;
      }) : [],

      // Examples
      examples: examples || [],

      // Structured output
      structured_output: structuredOutput || null,

      // Metadata
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    return payload;
  }
}

// Create and export singleton instance
const agentService = new AgentService();
export default agentService;
